import React from "react";
import type { Path } from "react-hook-form";
import type { UseFormReturn } from "react-hook-form";

import {
  Card,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components";
import { Checkbox } from "@/components/ui/checkbox";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/context/i18n";

import type { TProductFormData } from "../../../products.schema";
import {
  PLAN_ADJUSTMENT_TYPES,
  type TPlanAdjustmentEntry,
  type TPlanAdjustmentType,
} from "../../../schemas";

type ProductPath = Path<TProductFormData>;

function createDefaultAdjustment(
  type: TPlanAdjustmentType,
): TPlanAdjustmentEntry {
  return {
    adjustment_type: type,
    custom_label: "",
    new_product_value_enabled: false,
    new_product_value: undefined,
    new_commission_value_enabled: false,
    new_commission_value: undefined,
    assistance_description: "",
    fipe_table_percent: undefined,
  };
}

function sortByPlanSheetOrder(
  entries: TPlanAdjustmentEntry[],
): TPlanAdjustmentEntry[] {
  const order = new Map(
    PLAN_ADJUSTMENT_TYPES.map((t, i) => [t, i] as const),
  );
  return [...entries].sort(
    (a, b) =>
      (order.get(a.adjustment_type) ?? 0) - (order.get(b.adjustment_type) ?? 0),
  );
}

export interface IPlanAdjustmentsSectionProps {
  form: UseFormReturn<TProductFormData>;
  planIndex: number;
  /** Auto/Moto usa `plans`; Assistência SoulHub usa faixas `capital_ranges`. */
  parentField?: "plans" | "capital_ranges";
}

export const PlanAdjustmentsSection: React.FC<IPlanAdjustmentsSectionProps> = ({
  form,
  planIndex,
  parentField = "plans",
}) => {
  const { t } = useI18n();
  const base =
    `${parentField}.${planIndex}.plan_adjustments` as ProductPath;

  const adjustments =
    (form.watch(`${base}` as ProductPath) as TPlanAdjustmentEntry[] | undefined) ??
    [];

  const toggleType = React.useCallback(
    (type: TPlanAdjustmentType, checked: boolean) => {
      const current = (form.getValues(`${base}` as ProductPath) ??
        []) as TPlanAdjustmentEntry[];
      if (checked) {
        if (current.some((a) => a.adjustment_type === type)) return;
        const next = sortByPlanSheetOrder([
          ...current,
          createDefaultAdjustment(type),
        ]);
        form.setValue(`${base}` as ProductPath, next as never, {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        form.setValue(
          `${base}` as ProductPath,
          current.filter((a) => a.adjustment_type !== type) as never,
          { shouldDirty: true, shouldValidate: true },
        );
      }
    },
    [form, planIndex],
  );

  return (
    <div className="space-y-4 border-t border-border pt-6">
      <FormLabel className="text-base font-semibold">
        {t("products.vehicle.plan.adjustments.sectionTitle")}
      </FormLabel>
      <p className="text-sm text-muted-foreground">
        {t("products.vehicle.plan.adjustments.sectionHint")}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {PLAN_ADJUSTMENT_TYPES.map((type) => {
          const checked = adjustments.some((a) => a.adjustment_type === type);
          return (
            <label
              key={type}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3"
            >
              <Checkbox
                checked={checked}
                onCheckedChange={(c) => toggleType(type, c === true)}
              />
              <span className="text-sm font-medium">
                {t(`products.vehicle.plan.adjustments.types.${type}`)}
              </span>
            </label>
          );
        })}
      </div>

      <div className="space-y-6">
        {PLAN_ADJUSTMENT_TYPES.map((type) => {
          const idx = adjustments.findIndex((a) => a.adjustment_type === type);
          if (idx < 0) return null;

          const prefix = `${base}.${idx}` as const;

          return (
            <Card key={type} className="border border-border p-4 shadow-sm">
              <h4 className="mb-4 text-sm font-semibold">
                {t(`products.vehicle.plan.adjustments.types.${type}`)}
              </h4>

              {type === "lmi" && (
                <FormField
                  control={form.control}
                  name={`${prefix}.fipe_table_percent` as ProductPath}
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>
                        {t(
                          "products.vehicle.plan.adjustments.fipeTablePercent",
                        )}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            className="pr-8"
                            value={
                              field.value === undefined || field.value === null
                                ? ""
                                : String(field.value)
                            }
                            onChange={(e) => {
                              const raw = e.target.value;
                              field.onChange(
                                raw === "" ? undefined : Number(raw),
                              );
                            }}
                          />
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                            %
                          </span>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="grid gap-4 md:grid-cols-2">
                {type === "is" && (
                  <>
                    <FormField
                      control={form.control}
                      name={`${prefix}.new_product_value_enabled` as ProductPath}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 md:col-span-2">
                          <FormLabel className="!mt-0">
                            {t(
                              "products.vehicle.plan.adjustments.insuredSumToggle",
                            )}
                          </FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value === true}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    {adjustments[idx]?.new_product_value_enabled === true && (
                      <FormField
                        control={form.control}
                        name={`${prefix}.new_product_value` as ProductPath}
                        render={({ field }) => (
                          <FormItem className="md:col-span-2">
                            <FormLabel>
                              {t(
                                "products.vehicle.plan.adjustments.insuredSumValue",
                              )}
                            </FormLabel>
                            <FormControl>
                              <RealCurrencyInput
                                value={Number(field.value) || 0}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </>
                )}

                <FormField
                  control={form.control}
                  name={`${prefix}.new_commission_value_enabled` as ProductPath}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 md:col-span-2">
                      <FormLabel className="!mt-0">
                        {t(
                          "products.vehicle.plan.adjustments.newCommissionToggle",
                        )}
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value === true}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {adjustments[idx]?.new_commission_value_enabled === true && (
                  <FormField
                    control={form.control}
                    name={`${prefix}.new_commission_value` as ProductPath}
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>
                          {t(
                            "products.vehicle.plan.adjustments.newCommissionPercent",
                          )}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              className="pr-8"
                              value={
                                field.value === undefined || field.value === null
                                  ? ""
                                  : String(field.value)
                              }
                              onChange={(e) => {
                                const raw = e.target.value;
                                field.onChange(
                                  raw === "" ? undefined : Number(raw),
                                );
                              }}
                            />
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                              %
                            </span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
